import moment from "moment";
import { STORE_LINKS } from "src/constants/common";
import { STORAGE_KEYS } from "src/constants/common";
import { POST_MESSAGE_EVENTS } from "src/constants/common";
import { THEME_COLORS } from "../theme/colors";
import { GetLabTestCart } from "src/types/labTests.types";
import { getLabTestCartRequest } from "src/redux/slices/labTestSlice";
import { setCart } from "src/redux/slices/labTestSlice";
import { useAppDispatch } from "src/redux/hooks";
import storage from "./storage";

/** Convert height in cm to feet and inches (for device details form) */
export const convertToFeetInches = (
  cm: number
): { feet: number; inches: string } => {
  const totalInches = cm * 0.393701;
  const feet = Math.floor(totalInches / 12);
  const inches = (totalInches % 12).toFixed(2);
  return { feet, inches };
};

/** Convert feet + inches strings to height in cm (decimal string for API) */
export const formatFeetAndInchesToCm = (
  feet: string,
  inches: string
): number => {
  const feetValue = parseFloat(feet) || 0;
  const inchesValue = parseFloat(inches) || 0;
  return Math.round(feetValue * 30.48 + inchesValue * 2.54);
};

/** Calculate age in years from date of birth string */
export const calculateAge = (dob: string, format: string): number | null => {
  if (!dob || dob === "") return null;
  const birthDate = moment(dob, format).isValid() ? moment(dob, format) : null;
  if (!birthDate) return null;
  return moment().diff(birthDate, "years");
};

/**
 * Parses message payload from React Native sendMessageToWebView.
 * Native may send event.data as a JSON string (or double-stringified).
 */
export const parseMessageFromNative = (
  data: unknown
): Record<string, unknown> | null => {
  if (data == null) return null;
  try {
    let parsed: unknown = typeof data === "string" ? JSON.parse(data) : data;
    if (typeof parsed === "string") parsed = JSON.parse(parsed);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

export const sendMessageToParent = (eventName: string, data = {}) => {
  console.info("Send message to parent", eventName, data);
  try {
    if (window.ReactNativeWebView) {
      window?.ReactNativeWebView?.postMessage(
        JSON.stringify({ action: eventName, data })
      );
    } else {
      console.warn("ReactNativeWebView is not available on the window object.");
    }
  } catch (error) {
    console.error("sendMessageToParent ERROR", error);
  }
};

// react-native-webview (Android especially) can drop a postMessage call
// fired immediately on load, before the native listener is fully attached
// to the WebView instance — retry a couple of times shortly after so the
// very first status-bar color isn't silently lost. Returns a cleanup to
// clear the pending retries (call from the caller's effect cleanup).
export const sendStatusBarColor = (
  backgroundColor: string,
  style: "light-content" | "dark-content"
): (() => void) => {
  const send = () =>
    sendMessageToParent(POST_MESSAGE_EVENTS.SET_STATUS_BAR_COLOR, {
      backgroundColor,
      style,
    });

  send();
  const retryTimers = [300, 800].map((delay) => setTimeout(send, delay));

  return () => retryTimers.forEach(clearTimeout);
};

export const setThemeColors = (colors?: any) => {
  const themeColors = colors || THEME_COLORS;

  Object.entries(themeColors).forEach(([key, value]) => {
    document.documentElement.style.setProperty(
      // `--color-${key}`,
      `${key}`,
      value as string
    );
  });

  storage.setItem(STORAGE_KEYS.THEME_COLORS, JSON.stringify(themeColors));

  return colors;
};

export const parseQueryString = (queryString: string) => {
  var params = {};
  var queryStringWithoutQuestionMark = queryString.substring(1); // Remove the leading '?'
  var keyValuePairs = queryStringWithoutQuestionMark.split("&");

  for (var i = 0; i < keyValuePairs.length; i++) {
    var keyValuePair = keyValuePairs[i].split("=");
    var key = decodeURIComponent(keyValuePair[0]);
    var value =
      keyValuePair.length > 1 ? decodeURIComponent(keyValuePair[1]) : null;

    (params as Record<string, string | null>)[key] = value;
  }

  return params;
};

export const getUrlParams = () => {
  if (typeof window !== "undefined") {
    return parseQueryString(window.location.search);
  } else {
    return {};
  }
};

export const getBlurDataURL = (imageUrl: string) => {
  // For now, return a simple gray placeholder
  // In a real implementation, you might want to:
  // 1. Use a service like plaiceholder.co
  // 2. Generate a proper blurred version of the image
  // 3. Use a CDN that provides image transformations
  return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlZWVlZWUiLz48L3N2Zz4=";
};

export const formatDateInDDMMYYYY = (date: Date | null): string => {
  if (!date) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

export const formatDateToLocaleString = (
  date: Date | string | null
): string => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
};

export const formatDateToLocaleStringWithWeekDay = (
  date: Date | string | null
): string => {
  if (!date) return "";
  const selectedDate = new Date(date);

  // Arrays for manual formatting
  const weekdays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Extract parts
  const weekday = weekdays[selectedDate.getDay()];
  const day = selectedDate.getDate().toString().padStart(2, "0");
  const month = months[selectedDate.getMonth()];
  const year = selectedDate.getFullYear();

  // Final formatted string
  const formattedDate = `${weekday}, ${day} ${month}, ${year}`;

  return formattedDate;
};

export const getAppDownloadUrl = (
  app: "GOODFLIP_CARE_APP" | "GOODFLIP_APP"
) => {
  // android send to play store
  // ios send to app store
  if (navigator.userAgent.includes("Android")) {
    return STORE_LINKS[app].PLAY_STORE;
  } else if (
    navigator.userAgent.includes("iPhone") ||
    navigator.userAgent.includes("iPad")
  ) {
    return STORE_LINKS[app].APP_STORE;
  }
  return "";
};

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface Address {
  pincode?: string;
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  houseNumber?: string;
  sublocality?: string;
  locality?: string;
  administrative_area_level_3?: string;
}

export const getAddressComponents = (addressComponents: AddressComponent[]) => {
  const address: Address = {};
  //  houseNumber should include plus_code, sublocality, locality, administrative_area_level_3 - prepare in this format
  addressComponents.forEach((component: any) => {
    if (component.types.includes("postal_code")) {
      address.pincode = component.long_name;
    } else if (component.types.includes("country")) {
      address.country = component.long_name;
    } else if (component.types.includes("administrative_area_level_1")) {
      address.state = component.long_name;
    } else if (component.types.includes("locality")) {
      address.city = component.long_name;
    } else if (
      component.types.includes("administrative_area_level_2") &&
      !address.city
    ) {
      address.city = component.long_name;
    } else if (component.types.includes("plus_code")) {
      address.houseNumber = component.long_name;
    } else if (component.types.includes("sublocality")) {
      address.sublocality = component.long_name;
    } else if (component.types.includes("locality")) {
      address.locality = component.long_name;
    } else if (component.types.includes("administrative_area_level_3")) {
      address.administrative_area_level_3 = component.long_name;
    } else if (component.types.includes("route")) {
      address.street = component.long_name;
    }
  });

  const components = [
    address.houseNumber,
    address.sublocality,
    address.locality,
    address.administrative_area_level_3,
  ].filter(Boolean); // This removes any undefined or null values

  address.houseNumber = components.join(", ");

  return address;
};

export const useLabTestCart = () => {
  const dispatch = useAppDispatch();

  const fetchLabTestCart = async () => {
    try {
      const response = await new Promise<{ data: GetLabTestCart }>(
        (resolve, reject) => {
          dispatch(
            getLabTestCartRequest({
              resolve,
              reject,
              payload: {},
            })
          );
        }
      );

      dispatch(setCart(response.data));
      return response.data;
    } catch (error) {
      console.error("Failed to fetch lab test cart", error);
      throw error;
    }
  };

  return {
    fetchLabTestCart,
  };
};

export const razorPayKeyArr = () => {
  let data: String[] = ["razorpay_secret_test", "razorpay_key_id_test"];
  // if (Config.IS_LIVE_ENV) {
  //   data = ['razorpay_secret_live', 'razorpay_key_id_live'];
  // } else {
  //   data = ['razorpay_secret_test', 'razorpay_key_id_test'];
  // }
  return data;
};

export const getFormattedDateTime = (timestamp: string | undefined | null) => {
  if (!timestamp) return "";
  // 1 hour ago, 2 hour ago(today), dd/mm/yyyy • hh:mm A
  const diff = moment().diff(moment(timestamp), "hours");
  if (diff < 1) {
    return "Just now";
  } else if (diff < 24) {
    return `${diff} hours ago`;
    // } else if (diff < 48) {
    // return "Yesterday";
  } else {
    const date = moment(timestamp).format("DD/MM/YYYY");
    const time = moment(timestamp).format("hh:mm A");
    return `${date} • ${time}`;
  }
};
